import { useState } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import IconButton from '@zenbusiness/zui/IconButton';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { Page, pdfjs } from 'react-pdf';

import SaveAltIcon from '@mui/icons-material/SaveAlt';

import PinchZoom from '#client/components/PinchZoom';

import {
  ButtonStyled,
  ContainerStyled,
  DialogContentStyled,
  DialogStyled,
  DialogTitleStyled,
  DocumentStyled
} from './ViewPdfModal.styles';

import { getWidthOfCanvas } from './utils/screen';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const options = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`
};

const responsiveAllCanvas = () => {
  const allPages = document.querySelectorAll('.react-pdf__Page');

  if (allPages && allPages?.length > 0) {
    for (const page of allPages) {
      page.style.backgroundColor = 'transparent';
      const canvas = page.querySelector('.react-pdf__Page__canvas');
      if (canvas) {
        canvas.style.maxWidth = '1200px';
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.boxShadow = '0px 4px 16px 2px rgba(0, 0, 0, 0.12)';
        canvas.style.margin = '0 auto 24px auto';
      }
    }
  }
};

export default function ViewPdfModal({ title, handleClose, handleBack, link, onSave }) {
  const { isWebView } = WebView;
  const [totalPages, setTotalPages] = useState(1);

  const isWebview = isWebView;

  const onDocumentLoadSuccess = ({ numPages }) => {
    setTotalPages(numPages);
  };

  return (
    <DialogStyled
      disableRestoreFocus
      fullScreen
      onClose={handleClose}
      open
    >
      <DialogTitleStyled
        component="div"
        webview={isWebview ? 1 : 0}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <IconButton
            label="Go back to previous page"
            onClick={handleBack}
          >
            <SharedAsset
              alt=""
              src="/icon/micro/caret-left.svg"
            />
          </IconButton>
          <Text
            size="xl"
            sx={{ textAlign: 'center' }}
            weight="medium"
          >
            {title}
          </Text>
          <IconButton
            label="Close form modal"
            onClick={handleClose}
          >
            <SharedAsset
              alt=""
              src="/icon/micro/close-x.svg"
            />
          </IconButton>
        </Stack>
        {!isWebview && (
          <ContainerStyled>
            <Box
              display="flex"
              justifyContent="end"
              marginTop="32px"
            >
              <ButtonStyled
                icon={<SaveAltIcon />}
                onClick={onSave}
                variant="text"
              >
                Download
              </ButtonStyled>
            </Box>
          </ContainerStyled>
        )}
      </DialogTitleStyled>
      <DialogContentStyled>
        <PinchZoom>
          <DocumentStyled
            file={link}
            loading={<LinearProgress />}
            onLoadSuccess={onDocumentLoadSuccess}
            options={options}
          >
            {Array.from(new Array(totalPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                loading=""
                onLoadSuccess={() => {
                  if (index + 1 === totalPages) {
                    responsiveAllCanvas();
                  }
                }}
                pageNumber={index + 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                width={getWidthOfCanvas()}
              />
            ))}
          </DocumentStyled>
        </PinchZoom>
      </DialogContentStyled>
    </DialogStyled>
  );
}

ViewPdfModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleBack: PropTypes.func,
  title: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  onSave: PropTypes.func
};
